import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  Error,
  Loading,
  PlaceForm,
  ViewTitle,
} from '@iarc-programing/emanufac-components/src/components';
import {
  Backdrop,
  Button,
  Card,
  Chip,
  Fade,
  InputAdornment,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { config } from '@/configs';
import * as actions from '@/redux/actions';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  boxShadow: 24,
  p: 4,
};

const defaultValue = { name: '', address: '' };

function Place({ title, subtitle }) {
  const dispatch = useDispatch();
  const place = useSelector((state) => state.place);
  const information = useSelector((state) => state.information);
  const department = useSelector((state) => state.department);
  const materialType = useSelector((state) => state.materialType);
  const warehouse = useSelector((state) => state.warehouse);
  const history = useHistory();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(15);
  const [total, setTotal] = useState(undefined);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    dispatch(actions.placeAll({ name, page, size }));
    dispatch(
      actions.departmentAll({ name: '', page: 1, size: config.maxFetchSize }),
    );
    dispatch(actions.materialTypeAll({}));
    dispatch(
      actions.warehouseAll({
        page: 1,
        size: config.maxFetchSize,
      }),
    );
    return () => {};
  }, [name, page, size]);

  useEffect(() => {
    setTotal(place?.total);
    return () => {};
  }, [place]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setName(searchTerm);
      setPage(1);
    }, 700);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const renderTitle = () => <ViewTitle title={title} subtitle={subtitle} />;

  const onSubmit = async (data) => {
    try {
      console.log(data);
      await dispatch(actions.placeCreate(data));
      reset(defaultValue);
      alert('สำเร็จ');
      handleClose();
      await dispatch(actions.placeAll({ name, page, size }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeletePlace = async (id) => {
    const confirm = window.confirm('ยืนยันการลบข้อมูล');
    if (confirm) {
      try {
        await dispatch(actions.placeDelete(id));
        await dispatch(actions.placeAll({ name, page, size }));
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handlePushToEditPlace = async (id) => {
    history.push(`place/edit/${id}`);
  };

  const handleChangeRowsPerPage = (event) => {
    setSize(event.target.value);
    setPage(1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const renderAddButtom = () => (
    <div className="flex flex-row justify-end pb-4">
      <div>
        <Button variant="contained" onClick={handleOpen}>
          เพิ่ม
        </Button>
      </div>
    </div>
  );

  const renderSearch = () => (
    <Card>
      <div className="p-4 flex flex-row">
        <div className="w-full md:w-1/2">
          <TextField
            label="ค้นหา"
            fullWidth
            size={'small'}
            id="outlined-start-adornment"
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i className="fas fa-search"></i>
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>
    </Card>
  );

  const renderModal = () => (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Card sx={style} className="max-w-4xl">
          <div className="py-2">เพิ่มข้อมูล</div>
          <div className="py-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <PlaceForm
                control={control}
                Controller={Controller}
                errors={errors}
                department={department}
                information={information}
                materialType={materialType}
                watch={watch}
                allPlaces={place}
                warehouse={warehouse}
              />
              <div className="flex flex-row justify-end gap-1 py-4">
                <Button variant="contained" type="submit">
                  บันทึก
                </Button>
                <Button variant="outlined" onClick={handleClose}>
                  ยกเลิก
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </Fade>
    </Modal>
  );

  const renderTable = () => (
    <div className="my-2">
      <Paper>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <div className="font-bold">ลำดับที่</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> ชื่อสถานที่</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> ที่อยู่</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> การอนุญาต</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> ดำเนินการ</div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!_.isEmpty(place.rows) ? (
                place.rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {(page - 1) * size + index + 1}
                    </TableCell>
                    <TableCell>
                      {`${row.name}`}
                      {row?.wip_place && (
                        <Chip label="คลัง WIP" color="warning" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{`${row.address || '-'}`}</TableCell>
                    <TableCell>
                      {' '}
                      <div className="font-semibold mt-2">แผนกที่อนุญาติ</div>
                      {row?.allow_all_department ? (
                        'อนุญาตทุกแผนก'
                      ) : (
                        <div>
                          {_.map(
                            row?.allow_departments,
                            (eachDept, deptIndex) => (
                              <div key={deptIndex}>{eachDept?.name}</div>
                            ),
                          )}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold mt-2">
                          วัตถุดิบที่อนุญาติ
                        </div>
                        {!row?.inventory_restriction ? (
                          'อนุญาติทุกวัตถุดิบ'
                        ) : (
                          <div>
                            {_.map(
                              row?.allow_categories,
                              (eachCat, catIndex) => (
                                <div key={catIndex}>{eachCat?.name}</div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                      {row?.place_action?.enable && (
                        <div className="my-2">
                          ผูกกับกิจกรรมกับวัตถุดิบในคลัง
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-row flex-wrap gap-1">
                        <Button
                          variant="contained"
                          color={'warning'}
                          size={'small'}
                          onClick={() => handlePushToEditPlace(row.id)}
                        >
                          แก้ไข
                        </Button>
                        <Button
                          variant="contained"
                          color={'error'}
                          size={'small'}
                          onClick={() => {
                            handleDeletePlace(row?.id);
                          }}
                        >
                          ลบ
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="text-center">ไม่มีข้อมูล</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page - 1}
          count={total || 1}
          rowsPerPage={size}
          onPageChange={handleChangePage}
        />
      </Paper>
    </div>
  );

  if (place.isLoading) {
    return <Loading />;
  }
  if (!place.isLoading && place.isCompleted) {
    return (
      <div>
        {renderModal()}
        <div className="flex justify-between">
          <div>{renderTitle()}</div>
          <div className="mt-6">{renderAddButtom()}</div>
        </div>
        {renderSearch()}
        {renderTable()}
      </div>
    );
  }
  return <Error message={place?.message} />;
}

Place.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

Place.defaultProps = {
  title: '',
  subtitle: '',
};

export default Place;
